<template>
  <div class="oc-live-preview">
    <div class="oc-live-preview__head">
      <div class="d-flex align-items-center flex-wrap">
        <feather-icon icon="EyeIcon" size="16" class="text-primary mr-50" />
        <strong class="oc-live-preview__title">Live Preview</strong>
        <b-badge variant="light-primary" class="oc-live-preview__badge ml-50">
          Sample data
        </b-badge>
      </div>
      <div class="d-flex align-items-center">
        <b-spinner
          v-if="updating"
          small
          class="mr-50"
          label="Updating preview"
        />
        <small class="text-muted">Updates as you edit</small>
      </div>
    </div>

    <div class="oc-live-preview__stage">
      <iframe
        ref="frame"
        title="Offer letter live preview"
        class="oc-live-preview__frame"
        sandbox="allow-same-origin"
        :srcdoc="safeSrcdoc"
      />
      <div
        v-show="!hasContent"
        class="oc-live-preview__empty"
      >
        <feather-icon icon="FileTextIcon" size="36" class="mb-75 text-muted" />
        <p class="text-muted small mb-0">
          Add letter content to see a live sample preview here.
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import { BBadge, BSpinner } from 'bootstrap-vue'
import { stripScriptsFromPreviewHtml } from '@/utils/offer-template-editor'

export default {
  name: 'OfferConfigureLivePreview',
  components: {
    BBadge,
    BSpinner,
  },
  props: {
    srcdoc: {
      type: String,
      default: '',
    },
    emptySrcdoc: {
      type: String,
      default: '',
    },
    hasContent: {
      type: Boolean,
      default: false,
    },
    updating: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    // Final gate: page cards are static HTML/CSS; keep sandbox without allow-scripts.
    safeSrcdoc() {
      const raw = this.srcdoc || this.emptySrcdoc || ''
      return stripScriptsFromPreviewHtml(raw) || this.emptySrcdoc || ''
    },
  },
  watch: {
    safeSrcdoc: {
      immediate: true,
      handler(html) {
        this.$nextTick(() => this.applySrcdoc(html))
      },
    },
  },
  mounted() {
    this.applySrcdoc(this.safeSrcdoc)
  },
  methods: {
    applySrcdoc(html) {
      const frame = this.$refs.frame
      if (!frame) return
      const doc = html || this.emptySrcdoc || ''
      // Vue 2 binds srcdoc as an attribute; Chromium often does not reload
      // the document until the srcdoc *property* is assigned.
      if (frame.srcdoc !== doc) {
        frame.srcdoc = doc
      }
    },
  },
}
</script>

<style scoped>
.oc-live-preview {
  background: #fff;
  border: 1px solid #eef0f3;
  border-radius: 0.5rem;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  min-height: 320px;
}

.oc-live-preview__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 0.35rem;
  padding: 0.65rem 0.85rem;
  background: #f8f9fb;
  border-bottom: 1px solid #eef0f3;
}

.oc-live-preview__title {
  font-size: 0.875rem;
}

.oc-live-preview__badge {
  font-size: 0.625rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: #0f766e !important;
  background: rgba(0, 186, 176, 0.12) !important;
}

.oc-live-preview__stage {
  position: relative;
  background: #eef0f3;
  padding: 0.65rem;
  flex: 1 1 auto;
  min-height: 280px;
}

.oc-live-preview__frame {
  width: 100%;
  height: min(72vh, 720px);
  border: none;
  background: #e8eaed;
  display: block;
  border-radius: 0.25rem;
}

.oc-live-preview__empty {
  position: absolute;
  inset: 0.65rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 1.5rem;
  background: #fff;
  border-radius: 0.25rem;
}
</style>
